import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import BlacksmithWorkshop from '../components/BlacksmithWorkshop';
import ConnectWalletModal from '../components/ConnectWalletModal';
import { createICSpicyActor } from '../services/icspicy';
import { useAuth } from '../auth/useAuth';

interface FarmStats {
  pepper_plants: bigint;
  members: bigint;
  harvest_yield: string;
  co2_offset: string;
  last_updated: bigint;
}

export default function FarmPage() {
  const { agent, principal, isAuthenticated } = useAuth();
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [farmStats, setFarmStats] = useState<FarmStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    pepper_plants: '',
    members: '',
    harvest_yield: '',
    co2_offset: '',
  });
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (agent && principal) {
      loadFarmStats();
      checkAdmin();
    }
  }, [agent, principal]);

  const loadFarmStats = async () => {
    if (!agent) return;
    setLoading(true);
    try {
      const actor = await createICSpicyActor(agent);
      const stats = await actor.get_farm_stats();
      setFarmStats(stats);
      setEditForm({
        pepper_plants: stats.pepper_plants.toString(),
        members: stats.members.toString(),
        harvest_yield: stats.harvest_yield,
        co2_offset: stats.co2_offset,
      });
    } catch (err) {
      console.error('Failed to load farm stats:', err);
      setError('Failed to load farm statistics');
    } finally {
      setLoading(false);
    }
  };

  const checkAdmin = async () => {
    if (!agent || !principal) return;
    try {
      const actor = await createICSpicyActor(agent);
      const role = await actor.get_user_role_query(principal);
      setIsAdmin(role?.Admin !== undefined);
    } catch (err) {
      console.error('Failed to check admin status:', err);
    }
  };

  const handleUpdate = async () => {
    if (!agent) return;
    setUpdating(true);
    setError(null);
    try {
      const actor = await createICSpicyActor(agent);
      const pepper_plants = editForm.pepper_plants ? BigInt(editForm.pepper_plants) : null;
      const members = editForm.members ? BigInt(editForm.members) : null;
      const harvest_yield = editForm.harvest_yield || null;
      const co2_offset = editForm.co2_offset || null;

      await actor.update_farm_stats(pepper_plants, members, harvest_yield, co2_offset);
      setSuccess('Farm statistics updated successfully');
      setEditing(false);
      loadFarmStats();
    } catch (err: any) {
      setError(err.message || 'Failed to update farm statistics');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (timestamp: bigint) => {
    const date = new Date(Number(timestamp) / 1_000_000);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (!isAuthenticated) {
    return (
      <BlacksmithWorkshop>
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-4xl font-bold mb-4 text-gold">IC SPICY Farm</h1>
          <p className="text-gray-300 mb-6">Connect your wallet to view farm statistics</p>
          <button
            onClick={() => setShowConnectModal(true)}
            className="px-6 py-3 bg-gold text-black rounded-lg font-semibold hover:bg-yellow-500 transition"
          >
            Connect Wallet
          </button>
          {showConnectModal && (
            <ConnectWalletModal onClose={() => setShowConnectModal(false)} />
          )}
        </div>
      </BlacksmithWorkshop>
    );
  }

  return (
    <BlacksmithWorkshop>
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-gold via-yellow-500 to-gold bg-clip-text text-transparent">
            IC SPICY Farm
          </h1>
          <p className="text-xl text-gray-300">Real-World Asset Co-op Statistics</p>
        </motion.div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-900/50 border border-green-500 rounded-lg text-green-200">
            {success}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading farm statistics...</div>
        ) : farmStats ? (
          <div className="max-w-4xl mx-auto">
            {/* Farm Stats Display */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-dark rounded-2xl p-8 mb-6"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gold">Farm Statistics</h2>
                {isAdmin && (
                  <button
                    onClick={() => setEditing(!editing)}
                    className="px-4 py-2 bg-gold text-black rounded-lg font-semibold hover:bg-yellow-500 transition"
                  >
                    {editing ? 'Cancel' : 'Edit'}
                  </button>
                )}
              </div>

              {editing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-300 mb-2">Pepper Plants</label>
                    <input
                      type="number"
                      value={editForm.pepper_plants}
                      onChange={e => setEditForm({ ...editForm, pepper_plants: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Members</label>
                    <input
                      type="number"
                      value={editForm.members}
                      onChange={e => setEditForm({ ...editForm, members: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Harvest Yield</label>
                    <input
                      type="text"
                      value={editForm.harvest_yield}
                      onChange={e => setEditForm({ ...editForm, harvest_yield: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">CO2 Offset</label>
                    <input
                      type="text"
                      value={editForm.co2_offset}
                      onChange={e => setEditForm({ ...editForm, co2_offset: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <button
                    onClick={handleUpdate}
                    disabled={updating}
                    className="w-full py-3 bg-gold text-black rounded-lg font-semibold hover:bg-yellow-500 disabled:opacity-50"
                  >
                    {updating ? 'Updating...' : 'Update Statistics'}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="glass-dark rounded-xl p-6">
                    <div className="text-4xl mb-2">🌶️</div>
                    <div className="text-3xl font-bold text-gold mb-2">
                      {farmStats.pepper_plants.toString()}
                    </div>
                    <div className="text-gray-400">Pepper Plants</div>
                  </div>
                  <div className="glass-dark rounded-xl p-6">
                    <div className="text-4xl mb-2">👥</div>
                    <div className="text-3xl font-bold text-gold mb-2">
                      {farmStats.members.toString()}
                    </div>
                    <div className="text-gray-400">Members</div>
                  </div>
                  <div className="glass-dark rounded-xl p-6">
                    <div className="text-4xl mb-2">📦</div>
                    <div className="text-2xl font-bold text-gold mb-2">
                      {farmStats.harvest_yield}
                    </div>
                    <div className="text-gray-400">Harvest Yield</div>
                  </div>
                  <div className="glass-dark rounded-xl p-6">
                    <div className="text-4xl mb-2">🌱</div>
                    <div className="text-2xl font-bold text-gold mb-2">
                      {farmStats.co2_offset}
                    </div>
                    <div className="text-gray-400">CO2 Offset</div>
                  </div>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-gray-700 text-sm text-gray-400">
                Last updated: {formatDate(farmStats.last_updated)}
              </div>
            </motion.div>

            {/* Farm Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-dark rounded-2xl p-6"
            >
              <h3 className="text-2xl font-bold text-gold mb-4">About IC SPICY Farm</h3>
              <p className="text-gray-300 mb-4">
                IC SPICY is a Real-World Asset (RWA) co-op that combines digital NFTs with physical
                pepper farming. NFT holders are co-op members who share in the farm's success.
              </p>
              <p className="text-gray-300 mb-4">
                Our farm grows fresh Florida peppers, tracking everything from plant count to harvest
                yield and environmental impact. Each NFT represents ownership in the co-op and grants
                voting rights in the DAO.
              </p>
              <p className="text-gray-300">
                Join us in building a sustainable, community-driven agricultural co-op on the
                Internet Computer!
              </p>
            </motion.div>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-400">
            No farm statistics available
          </div>
        )}

        {showConnectModal && (
          <ConnectWalletModal onClose={() => setShowConnectModal(false)} />
        )}
      </div>
    </BlacksmithWorkshop>
  );
}

